import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core'
import { MbCheckoutNavigationService } from '../../services/mb-checkout-navigation.service'
import { Country, Currency, EventService, GlobalMessageService, GlobalMessageType, WindowRef } from '@spartacus/core'
import { BehaviorSubject, Observable, Subscription } from 'rxjs'
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators'
import { MbCheckoutQuoteInfoFacade } from '../../facades/mb-checkout-quote-info.facade'
import { Actions, ofType } from '@ngrx/effects'
import { MbCountriesNoAccountConnector } from '../../connectors/quote-info'
import { CountryLanguageService } from '../../../site-context/facade/country-language.service'
import { MbConstants } from '../../../shared/mb-constants'
import { MbCheckoutCommonService } from '../../services/mb-checkout-common.service'
import { MbCartMigrationFacade } from '../../../cart/facades/mb-cart-migration.facade'
import { MbCartActions } from '../../../cart/store/actions'
import { CookieService } from 'ngx-cookie-service'
import { MbAfterCartMigrationService } from '../../../cart/services/mb-after-cart-migration.service'
import { BaseSiteUtilsService } from '../../../shared/services/base-site-utils.service'
import { MbCheckoutTypes, MbQuoteInfoModel } from '../../../models'
import { ActiveCartFacade } from '@spartacus/cart/base/root'
import { CheckoutQuoteInfoSetEvent } from '../../../analytics/events/mb-checkout.events'

@Component({
  selector: 'mb-checkout-quote-info',
  templateUrl: './mb-checkout-quote-info.component.html',
  styleUrls: ['./mb-checkout-quote-info.component.scss']
})
export class MbCheckoutQuoteInfoComponent implements OnInit, OnDestroy {

  subscription: Subscription = new Subscription()
  shouldLeavePage = false
  checkoutType: string | undefined
  checkoutTypes = MbCheckoutTypes
  isDropdownMenuOpen = false
  selectedCountry: Country | undefined
  sourceCountry: Country | undefined
  targetCurrency: Currency | undefined
  constants = MbConstants
  activeSiteIso = ''
  cartCountry: string | undefined
  cartCode: string | undefined
  isTypeA$ = new BehaviorSubject<boolean>(false)
  selectedCountryName = ''
  states: Country[] = []

  search = (text$: Observable<string>) => text$.pipe(
    debounceTime(200),
    distinctUntilChanged(),
    map(term => {
      if (term?.length < 1) {
        return this.states.map(state => state?.name)
      } else {
        return this.states.filter(state => state?.name && state.name.toLowerCase().indexOf(term.toLowerCase()) > -1)
          .map(state => state?.name).slice(0, 10)
      }
    })
  )

  constructor (protected mbCheckoutNavigationService: MbCheckoutNavigationService,
               protected mbCheckoutQuoteInfoService: MbCheckoutQuoteInfoFacade,
               protected activeCartService: ActiveCartFacade,
               protected globalMessageService: GlobalMessageService,
               protected actions$: Actions,
               protected countriesNoAccountConnector: MbCountriesNoAccountConnector,
               protected countryLanguageService: CountryLanguageService,
               protected mbCheckoutCommonService: MbCheckoutCommonService,
               protected cdRef: ChangeDetectorRef,
               protected winRef: WindowRef,
               protected mbCartMigrationService: MbCartMigrationFacade,
               protected mbAfterCartMigrationService: MbAfterCartMigrationService,
               protected cookieService: CookieService,
               protected baseSiteUtilsService: BaseSiteUtilsService,
               protected eventService: EventService) {
  }

  ngOnInit () {
    this.subscription.add(this.activeCartService.getActive()
      .subscribe(cart => {
        if (this.shouldLeavePage) {
          this.mbCheckoutNavigationService.navigateToNextCheckoutStep()
          this.shouldLeavePage = false
        }

        this.cartCode = cart?.guid
        this.checkoutType = cart?.checkoutStatus?.checkoutType
        this.cartCountry = cart?.country?.isocode
        this.setCountry(cart?.country)
        this.cdRef.detectChanges()

        if (this.selectedCountry) {
          this.onCountrySelectionHandler(this.selectedCountry.name)
        }

        if (this.checkoutType === this.checkoutTypes.CHECKOUT_TYPE_A) {
          this.isTypeA$.next(true)
          this.getSelectionCountries(cart?.country?.isocode)
        }
      }))

    this.subscription.add(this.mbCheckoutCommonService.userCountry
      .subscribe((country: Country) => {
        if (this.sourceCountry !== country && this.selectedCountryName === '') {
          this.setCountry(country)
          this.onCountrySelectionHandler(country?.name)
        }
        this.sourceCountry = country
      }))

    this.subscription.add(this.countryLanguageService.getActive()
      .subscribe((activeBaseSite: string) => this.activeSiteIso = activeBaseSite?.split('-')?.[0]))

    this.subscription.add(this.eventService.get(CheckoutQuoteInfoSetEvent).subscribe(payload => {
      this.activeCartService.reloadActiveCart()
      if (!!payload?.isSuccessful && this.checkoutType === this.checkoutTypes.CHECKOUT_TYPE_A) {
        if (this.activeSiteIso === this.selectedCountry?.isocode || this.cartCountry === this.selectedCountry?.isocode) {
          this.shouldLeavePage = true
          return
        } else if (this.selectedCountry?.isocode) {
          this.getMigratedCartAndSetAsCurrentCart(this.selectedCountry)
        } else {
          this.globalMessageService.add({ key: 'quoteInfo.error' }, GlobalMessageType.MSG_TYPE_ERROR)
        }
      } else if (!!payload?.isSuccessful) {
        this.shouldLeavePage = true
      } else if (!payload?.isSuccessful) {
        this.globalMessageService.add({ key: 'quoteInfo.error' }, GlobalMessageType.MSG_TYPE_ERROR)
        console.error(payload?.error)
      }
      })
    )

    this.subscription?.add(this.actions$.pipe(
      ofType(MbCartActions.MIGRATE_CART_SUCCESS),
      map((action: MbCartActions.MigrateCartSuccess) => action?.payload))
      .subscribe((payload: any) => {
        if (payload?.migratedCart && Object.keys(payload?.migratedCart)) {
          const newBaseSite: string | undefined = this.countryLanguageService.getBaseSiteForCountry(this.selectedCountry?.isocode)
          const hasBaseSite: boolean = !!newBaseSite
          if (this.activeSiteIso !== this.constants.FALLBACK_COUNTRY || hasBaseSite) {
            this.mbAfterCartMigrationService.mapNewCartGuidToNewBaseSite(payload.migratedCart.guid,
              newBaseSite || this.constants.MB_BASE_SITE_NO_COUNTRY_KEY + this.constants.FALLBACK_COUNTRY)
            this.changeActiveBaseSite(hasBaseSite)
            this.shouldLeavePage = false
          } else {
            this.shouldLeavePage = true
          }
        } else {
          this.globalMessageService.add({ key: 'quoteInfo.migrationError' }, GlobalMessageType.MSG_TYPE_ERROR)
        }
      }))

    this.subscription?.add(this.actions$.pipe(
      ofType(MbCartActions.MIGRATE_CART_FAIL),
      map((action: MbCartActions.MigrateCartFail) => action?.payload))
      .subscribe(() => {
          this.globalMessageService.add({ key: 'quoteInfo.migrationError' }, GlobalMessageType.MSG_TYPE_ERROR)
        }
      ))
  }

  ngOnDestroy () {
    this.subscription?.unsubscribe()
  }

  setCountry (country?: Country) {
    if (country && Object.keys(country)?.length) {
      this.selectedCountry = country
      this.selectedCountryName = this.selectedCountry?.name ? this.selectedCountry?.name : ''
    }
  }

  back () {
    this.mbCheckoutNavigationService.navigateBackFromQuoteInfo()
  }

  next () {
    const quoteInfoDetails: MbQuoteInfoModel = {
      flag: true
    }
    if (this.checkoutType === this.checkoutTypes.CHECKOUT_TYPE_A) {
      this.handleCheckoutTypeANext(quoteInfoDetails)
    } else {
      this.mbCheckoutQuoteInfoService.postQuoteInfoDetails(quoteInfoDetails)
    }
  }

  onFocus (event: any) {
    if (!this.selectedCountry) {
      event.target.dispatchEvent(new Event('input'))
    }
  }

  getSelectionCountries (cartCountryIsocode?: string) {
    this.subscription.add(this.countriesNoAccountConnector.load().pipe(
        map(countries => countries.sort((a, b) => a.name && b.name && a.name.localeCompare(b.name) || 0))
      ).subscribe(countries => {
        this.states = countries
        if (!this.selectedCountry) {
          this.selectedCountry = this.states.find(country => country.isocode === cartCountryIsocode)
        }
        this.cdRef.detectChanges()
      })
    )
  }

  onCountrySelectionHandler (countryName?: string) {
    if (countryName && this.states.length > 0) {
      this.selectedCountry = this.states.find(state => state.name === countryName)
      this.cdRef.detectChanges()
      this.subscription.add(this.baseSiteUtilsService.getCurrencyOfCountry(this.selectedCountry?.isocode)
        .subscribe(currency => this.targetCurrency = currency))
    }
  }

  onDropdownMenuOpenHandler (isDropdownMenuOpen: boolean) {
    this.isDropdownMenuOpen = isDropdownMenuOpen
  }

  handleCheckoutTypeANext (quoteInfoDetails?: MbQuoteInfoModel) {
    if (!this.selectedCountry || !this.selectedCountry?.isocode) {
      this.globalMessageService.add({ key: 'quoteInfo.selectCountry' }, GlobalMessageType.MSG_TYPE_ERROR)
      return
    }

    quoteInfoDetails = {
      ...quoteInfoDetails,
      countryIsocode: this.selectedCountry?.isocode
    }

    this.mbCheckoutQuoteInfoService.postQuoteInfoDetails(quoteInfoDetails)

  }

  changeActiveBaseSite (hasBaseSite: boolean) {
    const sourceCountryName = this.sourceCountry &&
    Object.keys(this.sourceCountry).length && this.activeSiteIso !== this.constants.FALLBACK_COUNTRY ?
      this.sourceCountry.name : this.constants.INTERNATIONAL
    let targetCountryName: string | undefined = this.selectedCountry?.name
    if (hasBaseSite && this.selectedCountry?.isocode) {
      this.countryLanguageService.setActive(this.selectedCountry?.isocode, this.targetCurrency?.isocode)
      this.mbAfterCartMigrationService.updateLocationCookies(this.selectedCountry?.isocode)
    } else {
      targetCountryName = this.constants.INTERNATIONAL
      this.countryLanguageService.setActive(this.constants.FALLBACK_COUNTRY, this.constants.FALLBACK_CURRENCY)
      this.mbAfterCartMigrationService.updateLocationCookies(this.constants.FALLBACK_COUNTRY)
    }

    this.mbCheckoutNavigationService.navigateToCartPage( { key: 'mbcart.cartMigrationMessage', params: { sourceCountry: sourceCountryName, targetCountry: targetCountryName } })
  }

  getMigratedCartAndSetAsCurrentCart (newCountry: Country) {
    this.shouldLeavePage = false
    if (this.selectedCountry?.isocode) {
      this.mbCartMigrationService.migrateCart(this.cartCode as string, newCountry)
    } else {
      this.globalMessageService.add({ key: 'quoteInfo.selectCountry' }, GlobalMessageType.MSG_TYPE_ERROR)
    }
  }

  updatedSelectedCountryOnBlur () {
    if (!this.selectedCountryName) {
      this.selectedCountry = undefined
    }
  }
}
